<template>
  <div class="info-brinde">
    <!-- Nenhuma tag classificada como Brinde no evento -->
    <div v-if="!giftCatalog.length" class="ib-nostation">
      <div class="ib-warn">
        <i class="bx bx-error-circle"></i>
        Nenhum brinde cadastrado. Em <b>Tags</b>, crie/edite uma tag e marque a classificação como <b>Brinde</b> para ela aparecer aqui.
      </div>
    </div>

    <template v-else>
      <!-- Progresso (fixo) — só faz sentido com mais de 1 brinde no leitor.
           Conta sobre os brindes ELEGÍVEIS (a que o participante tem direito). -->
      <div v-if="showProgress" class="ib-progress">
        <div class="progress-top">
          <span class="progress-label">Retiradas</span>
          <span class="progress-count">{{ deliveredCount }} de {{ eligibleCount }}</span>
        </div>
        <div class="progress-track">
          <div class="progress-fill" :class="{ complete: isComplete }" :style="{ width: percent + '%' }"></div>
        </div>
      </div>

      <!-- Abas (fixo) -->
      <div class="ib-tabs">
        <button
          type="button"
          class="ib-tab"
          :class="{ active: tab === 'brindes' }"
          @click="tab = 'brindes'"
        >Brindes</button>
        <button
          type="button"
          class="ib-tab"
          :class="{ active: tab === 'historico' }"
          @click="tab = 'historico'"
        >
          Histórico
          <span v-if="history.length" class="ib-tab-count">{{ history.length }}</span>
        </button>
      </div>

      <!-- Corpo rolável -->
      <div class="ib-body">
        <!-- Aba Brindes -->
        <div v-show="tab === 'brindes'">
          <div
            v-for="g in sortedGifts"
            :key="g.id"
            class="brinde-card"
            :class="[cardClass(g), { scanned: g.scanned }]"
          >
            <div class="brinde-card-top">
              <div class="brinde-icon">
                <!-- retirado: check ; elegível-pendente: brinde ; não elegível: cadeado -->
                <svg v-if="g.state === 'delivered'" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 8l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else-if="g.state === 'ineligible'" width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <rect x="3" y="8" width="10" height="6" rx="1.5" stroke="currentColor" stroke-width="1.3"/>
                  <path d="M5 8V5.5a3 3 0 016 0V8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
                </svg>
                <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 7h10v6a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" stroke="currentColor" stroke-width="1.3"/>
                  <path d="M2 5h12v2H2zM8 5v10" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="brinde-info">
                <div class="brinde-name">
                  {{ g.name }}
                  <span v-if="g.scanned" class="brinde-scanned-badge">Lido agora</span>
                  <span v-if="g.isBonus" class="brinde-bonus-badge">Bônus</span>
                </div>
                <div class="brinde-meta">
                  <span class="brinde-status-dot"></span>
                  {{ statusText(g) }}
                </div>
                <div v-if="g.state === 'delivered' && g.deliveredAt" class="brinde-delivered-info">
                  {{ formatWhen(g.deliveredAt) }}<template v-if="g.operator"> · por {{ g.operator }}</template>
                </div>
              </div>
            </div>

            <div class="brinde-action">
              <!-- Não elegível: autoriza a tag (se no escopo do operador) -->
              <button
                v-if="g.state === 'ineligible' && g.canAuthorize"
                type="button"
                class="btn-authorize"
                :disabled="savingId === g.id"
                @click="authorize(g)"
              >
                <i v-if="savingId === g.id" class="bx bx-loader-alt bx-spin"></i>
                <svg v-else width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                Autorizar retirada
              </button>
              <!-- Não elegível e fora do escopo: só informa -->
              <div v-else-if="g.state === 'ineligible'" class="brinde-noscope">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                  <rect x="3" y="8" width="10" height="6" rx="1.5" stroke="currentColor" stroke-width="1.3"/>
                  <path d="M5 8V5.5a3 3 0 016 0V8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
                </svg>
                Sem a tag deste brinde — autorização fora do seu escopo
              </div>
              <!-- Elegível, não retirado: entrega -->
              <button
                v-else-if="g.state === 'pending'"
                type="button"
                class="btn-deliver"
                :disabled="savingId === g.id"
                @click="mark(g, false)"
              >
                <i v-if="savingId === g.id" class="bx bx-loader-alt bx-spin"></i>
                <svg v-else width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M4 8l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Entregar
              </button>
              <!-- Retirado: desfaz -->
              <button
                v-else
                type="button"
                class="btn-undo"
                :disabled="savingId === g.id"
                @click="mark(g, true)"
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M4 7h6a3 3 0 010 6H7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M7 4L4 7l3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Desfazer
              </button>
            </div>
          </div>
        </div>

        <!-- Aba Histórico -->
        <div v-show="tab === 'historico'">
          <div v-if="!history.length" class="empty">
            <div class="empty-icon">
              <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
                <path d="M3 7h10v6a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" stroke="currentColor" stroke-width="1.3"/>
                <path d="M2 5h12v2H2zM8 5v10" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="empty-title">Nenhuma retirada registrada</div>
            <div class="empty-sub">As retiradas de brinde deste participante aparecerão aqui.</div>
          </div>
          <div v-else>
            <div v-for="(h, i) in history" :key="i" class="h-item">
              <div class="h-icon" :class="h.undone ? 'desfeito' : 'entregue'">
                <svg v-if="h.undone" width="12" height="12" viewBox="0 0 16 16" fill="none">
                  <path d="M4 7h6a3 3 0 010 6H7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M7 4L4 7l3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M4 8l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="h-info">
                <div class="h-top">
                  <div class="h-brinde-name">{{ h.name }}</div>
                  <div class="h-date">{{ formatTime(h.at) }}</div>
                </div>
                <div class="h-detail">
                  {{ h.undone ? 'Desfeito' : 'Entregue' }} por <span class="h-operator">{{ h.by || 'Operador' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Swal from 'sweetalert2'
import { CHECK_IN_MODULE } from '@/modules/check-in'
import { markGift, grantGift } from '@/modules/check-in/service/markGift'
import { successMessage, errorMessage } from '@/components/alertsMessages'

export default {
  name: 'info-brinde',
  data: () => ({ tab: 'brindes', savingId: null }),
  computed: {
    ...mapGetters(CHECK_IN_MODULE, ['giftCatalog', 'scannedGiftTagId']),
    currentId () {
      return this.$store.state[CHECK_IN_MODULE].showDetailId
    },
    item () {
      return this.$store.getters[CHECK_IN_MODULE + '/detailById'](this.currentId)
    },
    // Status POR BRINDE. Dois conceitos SEPARADOS:
    //  - elegibilidade = TER a tag do brinde (isChecked). É o direito de receber.
    //  - retirada      = registro à parte (atividade). "Retirado" = a última
    //    movimentação daquela tag é uma retirada (GiftCollected, não desfeita).
    // Estados: ineligible (sem a tag) / pending (tem a tag, não retirou) /
    //          delivered (retirou).
    // canAuthorize = a tag está no escopo do operador (pode conceder/Autorizar).
    gifts () {
      const tags = this.giftCatalog || []
      const itemTags = (this.item && this.item.tags) || []
      const activities = (this.item && this.item.activitiesList) || []
      return tags.map(g => {
        const it = itemTags.find(t => t.id === g.id)
        const eligible = !!(it && it.isChecked)
        // Bônus = tag concedida pelo operador (autorização). É retirável, mas não
        // é direito/default: não conta pro progresso nem pro status "Entregue".
        const isBonus = !!(it && it.isBonus)
        // Fora do item.tags = tag não atribuída E fora do escopo → não pode autorizar.
        const canAuthorize = !!(it && it.isEditable !== false)
        const acts = activities
          .filter(a => (a.eventType === 'GiftCollected' || a.eventType === 'GiftRevoked') && a.tagId === g.id)
          .slice()
          .sort((a, b) => new Date(b.at) - new Date(a.at))
        const lastAct = acts[0]
        const delivered = !!(lastAct && lastAct.eventType === 'GiftCollected')
        const state = delivered ? 'delivered' : (eligible ? 'pending' : 'ineligible')
        // Brinde "lido agora" pelo leitor NESTA abertura da ficha (transitório —
        // limpa ao fechar). Ficha aberta pela lista não acende o selo.
        const scanned = this.scannedGiftTagId != null && this.scannedGiftTagId === g.id
        return {
          id: g.id,
          name: g.name || (it && it.text) || ('#' + g.id),
          state,
          eligible,
          isBonus,
          delivered,
          canAuthorize,
          scanned,
          deliveredAt: delivered ? lastAct.at : null,
          operator: delivered ? lastAct.by : null
        }
      })
    },
    // O brinde LIDO pelo leitor primeiro; depois de-direito, e bônus por último.
    sortedGifts () {
      const rank = g => (g.state === 'pending' ? 0 : (g.state === 'ineligible' ? 1 : 2))
      return this.gifts.slice().sort((a, b) => {
        if (a.scanned !== b.scanned) return a.scanned ? -1 : 1
        if (!!a.isBonus !== !!b.isBonus) return a.isBonus ? 1 : -1
        return rank(a) - rank(b)
      })
    },
    // Brindes de DIREITO (não-bônus) que o participante tem: base do progresso e
    // do "completo". Bônus são extras — não entram na conta.
    requiredGifts () {
      return this.gifts.filter(g => g.eligible && !g.isBonus)
    },
    eligibleCount () {
      return this.requiredGifts.length
    },
    deliveredCount () {
      return this.requiredGifts.filter(g => g.delivered).length
    },
    // Progresso só faz sentido com mais de 1 brinde no leitor.
    showProgress () {
      return this.gifts.length > 1
    },
    percent () {
      return this.eligibleCount ? Math.round((this.deliveredCount / this.eligibleCount) * 100) : 0
    },
    isComplete () {
      return this.eligibleCount > 0 && this.deliveredCount === this.eligibleCount
    },
    history () {
      const list = (this.item && this.item.activitiesList) || []
      return list
        .filter(a => a.eventType === 'GiftCollected' || a.eventType === 'GiftRevoked')
        .slice()
        .sort((a, b) => new Date(b.at) - new Date(a.at))
        .map(a => ({
          name: a.details || 'Brinde',
          at: a.at,
          by: a.by,
          undone: a.eventType === 'GiftRevoked'
        }))
    }
  },
  watch: {
    currentId () { this.tab = 'brindes' }
  },
  methods: {
    cardClass (g) {
      return g.state // 'pending' | 'delivered' | 'ineligible'
    },
    statusText (g) {
      if (g.state === 'delivered') return 'Entregue'
      if (g.state === 'ineligible') return 'Não elegível — sem a tag'
      return 'Aguardando retirada'
    },
    formatTime (d) {
      const dt = d instanceof Date ? d : new Date(d)
      if (isNaN(dt.getTime())) return ''
      const pad = n => ('' + n).padStart(2, '0')
      return `${pad(dt.getHours())}:${pad(dt.getMinutes())}`
    },
    formatWhen (d) {
      const dt = d instanceof Date ? d : new Date(d)
      if (isNaN(dt.getTime())) return ''
      const pad = n => ('' + n).padStart(2, '0')
      const hhmm = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`
      const today = new Date()
      const sameDay = dt.getDate() === today.getDate() &&
        dt.getMonth() === today.getMonth() && dt.getFullYear() === today.getFullYear()
      return sameDay ? `Hoje às ${hhmm}` : `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)} às ${hhmm}`
    },
    // Autoriza a retirada = concede a tag (elegibilidade). Habilita o "Entregar".
    async authorize (gift) {
      if (!gift || !gift.canAuthorize || !this.currentId) return
      const nome = (this.item && this.item.name) || 'o participante'
      const r = await Swal.fire({
        title: 'Autorizar retirada?',
        html: `Conceder a tag de <b>${gift.name}</b> a <b>${nome}</b>? Isso o torna elegível a retirar este brinde.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#e8860c',
        cancelButtonColor: '#74788d',
        confirmButtonText: 'Sim, autorizar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      })
      if (!r.value) return

      this.savingId = gift.id
      try {
        const res = await grantGift(this.currentId, gift.id, true)
        if (res.ok) {
          successMessage(res.message || 'Elegibilidade concedida.')
          this.$store.dispatch(CHECK_IN_MODULE + '/fetchDetail', { id: this.currentId })
        } else {
          errorMessage(res.message || 'Não foi possível autorizar.')
        }
      } catch (e) {
        errorMessage('Não foi possível autorizar.')
      } finally {
        this.savingId = null
      }
    },
    async mark (gift, remove) {
      if (!gift || !this.currentId) return
      // Entregar exige elegibilidade; desfazer exige estar retirado.
      if (!remove && !gift.eligible) return
      if (remove && !gift.delivered) return

      if (remove) {
        const r = await Swal.fire({
          title: 'Desfazer retirada?',
          html: `Reverter a retirada de <b>${gift.name}</b>? A elegibilidade (tag) é mantida.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#f46a6a',
          cancelButtonColor: '#74788d',
          confirmButtonText: 'Sim, desfazer',
          cancelButtonText: 'Cancelar',
          reverseButtons: true
        })
        if (!r.value) return
      }

      this.savingId = gift.id
      try {
        const res = await markGift(this.currentId, gift.id, remove)
        if (res.ok) {
          successMessage(res.message || (remove ? 'Retirada desfeita.' : 'Brinde entregue.'))
          // Recarrega o detalhe pra refletir a retirada e o histórico atualizados.
          this.$store.dispatch(CHECK_IN_MODULE + '/fetchDetail', { id: this.currentId })
        } else {
          errorMessage(res.message || 'Não foi possível registrar.')
        }
      } catch (e) {
        errorMessage('Não foi possível registrar.')
      } finally {
        this.savingId = null
      }
    }
  }
}
</script>

<style scoped>
.info-brinde {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.ib-nostation { padding: 20px 24px; }
.ib-warn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px;
  border-radius: 8px;
  background: #fff3e0;
  color: #b8801f;
  font-size: 13px;
}

/* ── Progresso (fixo) ── */
.ib-progress {
  padding: 16px 24px;
  border-bottom: 1px solid #eef0f4;
  flex-shrink: 0;
}
.progress-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.progress-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #4a5068;
}
.progress-count {
  font-size: 0.75rem;
  font-weight: 700;
  color: #10b981;
  font-variant-numeric: tabular-nums;
}
.progress-track {
  width: 100%;
  height: 6px;
  background: #eef0f4;
  border-radius: 3px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: #10b981;
  border-radius: 3px;
  transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.progress-fill.complete {
  background: linear-gradient(90deg, #10b981, #059669);
}

/* ── Abas (fixo) ── */
.ib-tabs {
  display: flex;
  border-bottom: 1px solid #eef0f4;
  flex-shrink: 0;
}
.ib-tab {
  flex: 1;
  padding: 14px 16px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #b8bcc8;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}
.ib-tab.active { color: #e8860c; border-bottom-color: #e8860c; }
.ib-tab:hover:not(.active) { color: #7b8294; }
.ib-tab-count {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 6px;
  background: #eef0f4;
  color: #9ca3b4;
}
.ib-tab.active .ib-tab-count { background: #fff3e0; color: #e8860c; }

/* ── Corpo rolável ── */
.ib-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
}
.ib-body::-webkit-scrollbar { width: 4px; }
.ib-body::-webkit-scrollbar-track { background: transparent; }
.ib-body::-webkit-scrollbar-thumb { background: #a2adb7; border-radius: 7px; }

/* ── Card de brinde ── */
.brinde-card {
  border: 1.5px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 10px;
  transition: all 0.2s;
}
.brinde-card:last-child { margin-bottom: 0; }
.brinde-card.pending:hover {
  border-color: #d4d8e3;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
}
.brinde-card.delivered { background: #f0fdf6; border-color: #bbf7d0; }
.brinde-card.ineligible { background: #fbfbfc; border-color: #e7e9f0; }
.brinde-scanned-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 6px;
  background: #fff3e0;
  color: #e8860c;
  font-size: 0.5625rem;
  font-weight: 700;
  vertical-align: middle;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.brinde-bonus-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 6px;
  background: #eef1fe;
  color: #4f6ef7;
  font-size: 0.5625rem;
  font-weight: 700;
  vertical-align: middle;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.brinde-card-top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.brinde-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.brinde-card.pending .brinde-icon { background: #fff3e0; color: #e8860c; }
.brinde-card.delivered .brinde-icon { background: rgba(16, 185, 129, 0.12); color: #10b981; }
.brinde-card.ineligible .brinde-icon { background: #eef0f4; color: #b8bcc8; }

.brinde-info { flex: 1; min-width: 0; }
.brinde-name {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.brinde-card.ineligible .brinde-name { color: #7b8294; }
.brinde-meta {
  font-size: 0.6875rem;
  color: #9ca3b4;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.brinde-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.brinde-card.pending .brinde-status-dot { background: #e8860c; }
.brinde-card.delivered .brinde-status-dot { background: #10b981; }
.brinde-card.ineligible .brinde-status-dot { background: #c8ccd8; }
.brinde-delivered-info {
  font-size: 0.6875rem;
  color: #059669;
  margin-top: 2px;
}

/* Ação */
.brinde-action { display: flex; gap: 8px; }
.btn-authorize {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #e8860c;
  color: #fff;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
}
.btn-authorize:hover:not(:disabled) { background: #cf7808; }
.btn-authorize:disabled { opacity: 0.65; cursor: default; }
.btn-authorize i { font-size: 15px; }
.brinde-noscope {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 12px;
  border-radius: 8px;
  background: #f4f5f8;
  color: #9ca3b4;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.35;
}
.btn-deliver {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #10b981;
  color: #fff;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
}
.btn-deliver:hover:not(:disabled) { background: #059669; }
.btn-deliver:disabled { opacity: 0.65; cursor: default; }
.btn-deliver i { font-size: 15px; }
.btn-undo {
  padding: 10px 14px;
  border: 1.5px solid #e4e7ed;
  border-radius: 8px;
  background: #fff;
  color: #7b8294;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
}
.btn-undo:hover:not(:disabled) { border-color: #c0c4d0; background: #f8f9fb; }
.btn-undo:disabled { opacity: 0.65; cursor: default; }

/* ── Histórico ── */
.h-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: #f8f9fb;
  border-radius: 8px;
  margin-bottom: 8px;
}
.h-item:last-child { margin-bottom: 0; }
.h-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.h-icon.entregue { background: rgba(16, 185, 129, 0.1); color: #10b981; }
.h-icon.desfeito { background: rgba(239, 68, 68, 0.08); color: #ef4444; }
.h-info { flex: 1; min-width: 0; }
.h-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.h-brinde-name { font-size: 0.8125rem; font-weight: 700; color: #1a1d26; }
.h-date { font-size: 0.625rem; color: #b8bcc8; flex-shrink: 0; font-variant-numeric: tabular-nums; }
.h-detail { font-size: 0.6875rem; color: #7b8294; margin-top: 3px; }
.h-operator { font-weight: 600; color: #4a5068; }

/* ── Empty ── */
.empty { text-align: center; padding: 40px 20px; }
.empty-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: #f4f5f8;
  color: #c0c4d0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}
.empty-title { font-size: 0.8125rem; font-weight: 700; color: #7b8294; }
.empty-sub { font-size: 0.6875rem; color: #b8bcc8; margin-top: 4px; line-height: 1.5; }
</style>
